import { useRef, useState } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';
import Tooltip from 'app/components/flat/ui/Tooltip';
import { PasswordVisibilityIcon } from 'app/components/core/icons';
import * as S from './InputPassword.styles';

type InputPasswordProps = {
  id?: string;
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  showVisibilityToggle?: boolean;
  errorMessage?: string;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  'aria-label'?: string;
  'data-testid'?: string;
};

let inputPasswordIdCounter = 0;

function useGeneratedInputId() {
  const idRef = useRef<string>();
  if (!idRef.current) {
    inputPasswordIdCounter += 1;
    idRef.current = `input-password-${inputPasswordIdCounter}`;
  }
  return idRef.current;
}

const InputPassword = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  showVisibilityToggle = false,
  errorMessage,
  onFocus,
  onBlur,
  autoComplete = 'current-password',
  'aria-label': ariaLabel,
  'data-testid': testId,
}: InputPasswordProps) => {
  const generatedId = useGeneratedInputId();
  const finalId = id ?? generatedId;
  const [isVisible, setIsVisible] = useState(false);
  const visibilityLabel = isVisible ? 'Ocultar senha' : 'Mostrar senha';
  const hasError = Boolean(errorMessage);
  const errorMessageId = `${finalId}-error`;

  return (
    <S.Field>
      <S.Label htmlFor={finalId}>
        <S.LabelText>{label}</S.LabelText>
        {required ? <S.RequiredMark>*</S.RequiredMark> : null}
      </S.Label>
      <S.InputWrapper $hasError={hasError}>
        <S.Input
          id={finalId}
          type={isVisible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-label={ariaLabel ?? label}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorMessageId : undefined}
          data-testid={testId}
        />
        {showVisibilityToggle ? (
          <Tooltip title={visibilityLabel} placement="top" arrow>
            <S.ToggleButtonWrap>
              <S.ToggleButton
                type="button"
                data-testid="password-visibility-toggle"
                onClick={() => setIsVisible((current) => !current)}
                disabled={disabled}
                aria-label={visibilityLabel}
              >
                <PasswordVisibilityIcon visible={isVisible} />
              </S.ToggleButton>
            </S.ToggleButtonWrap>
          </Tooltip>
        ) : null}
      </S.InputWrapper>
      {hasError ? (
        <S.ErrorMessage id={errorMessageId} role="alert">
          {errorMessage}
        </S.ErrorMessage>
      ) : null}
    </S.Field>
  );
};

export default InputPassword;
